import * as React from "react";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useI18n } from "../i18n/I18nProvider";


export default function Welcome(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const { lang, setLang, t } = useI18n();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        bgcolor: "#05070B",
        color: "#FFFFFF",
        backgroundImage:
          "linear-gradient(90deg, rgba(5,7,11,0.96) 0%, rgba(5,7,11,0.80) 38%, rgba(64,6,10,0.34) 66%, rgba(127,29,29,0.18) 100%), linear-gradient(180deg, rgba(5,7,11,0.08) 0%, rgba(5,7,11,0.78) 100%), url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: { xs: "62% center", md: "center" },
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 72% 35%, rgba(220,38,38,0.24), transparent 32%), radial-gradient(circle at 42% 14%, rgba(185,28,28,0.18), transparent 30%)",
          pointerEvents: "none",
        }}
      />

      <Stack
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: { xs: "calc(100vh - 32px)", md: "calc(100vh - 48px)" },
          width: "100%",
          maxWidth: 1180,
          mx: "auto",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              component="img"
              src="/logo.png"
              alt="takeKONTROL"
              sx={{
                width: 42,
                height: 42,
                objectFit: "contain",
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.92)",
                p: 0.55,
              }}
            />
            <Typography sx={{ fontWeight: 900, color: "#FFFFFF" }}>
              takeKONTROL
            </Typography>
          </Stack>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={lang}
            onChange={(_, next) => {
              if (next) setLang(next);
            }}
            sx={{
              bgcolor: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 999,
              overflow: "hidden",
              backdropFilter: "blur(14px)",
              "& .MuiToggleButton-root": {
                px: 1.2,
                py: 0.5,
                fontWeight: 900,
                textTransform: "none",
                border: 0,
                color: "rgba(255,255,255,0.72)",
              },
              "& .Mui-selected": {
                color: "#FFFFFF",
                bgcolor: "#DC2626",
              },
              "& .Mui-selected:hover": {
                bgcolor: "#B91C1C",
              },
            }}
          >
            <ToggleButton value="en">{t("lang.en")}</ToggleButton>
            <ToggleButton value="de">{t("lang.de")}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack
          spacing={3}
          justifyContent="center"
          sx={{
            flex: 1,
            width: "100%",
            maxWidth: 650,
            py: { xs: 6, md: 8 },
          }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                color: "#FFFFFF",
                fontSize: { xs: 44, md: 68 },
                lineHeight: 0.98,
                maxWidth: 620,
              }}
            >
              {lang === "de"
              ? "Stärke beginnt mit Klarheit"
              : "Readiness Program"}
            </Typography>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.82)",
                fontSize: { xs: 18, md: 22 },
                lineHeight: 1.55,
                mt: 2,
                maxWidth: 570,
              }}
            >
              {t("welcome.message")}
            </Typography>
          </Box>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.68)",
              fontWeight: 700,
              lineHeight: 1.6,
              maxWidth: 540,
            }}
          >
            {lang === "de"
              ? "Ein ruhiger Startpunkt für persönliche Vorbereitung, Resilienz und klare nächste Schritte."
              : "A calm starting point for personal preparedness, resilience, and clear next steps."}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => handlePageChange("Entry")}
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 3,
                py: 1.25,
                borderRadius: 2.5,
                bgcolor: "#DC2626",
                color: "#FFFFFF",
                fontWeight: 900,
                textTransform: "none",
                boxShadow: "0 16px 34px rgba(220,38,38,0.28)",
                "&:hover": {
                  bgcolor: "#B91C1C",
                  boxShadow: "0 18px 38px rgba(220,38,38,0.34)",
                },
              }}
            >
              {t("welcome.start")}
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "rgba(255,255,255,0.70)",
                fontWeight: 800,
                px: { xs: 0, sm: 1 },
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  bgcolor: "#EF4444",
                  boxShadow: "0 0 0 5px rgba(220,38,38,0.18)",
                  flexShrink: 0,
                }}
              />
              {lang === "de" ? "Bereit, Schritt für Schritt." : "Ready, step by step."}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
